import { useQuery } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import { z } from "zod";
import {
  ReactQueryOptions,
  RouterInputs,
  RouterOutputs,
  trpc,
} from "@utils/trpc";
import { useData } from "../../runtime/provider";
import type { DataSession } from "../../data/core/session";
import { NoAuthError } from "@utils/NoAuthError";
import { waitForAuthReady } from "@utils/auth/waitForAuthReady";
import { useInteractionReady } from "@hooks/useInteractionReady";
import { artistCommunityWireSchema } from "@libs/wire";

type QueryInputs = RouterInputs["user"]["stats"]["getArtistCommunity"];
type QueryOptions = ReactQueryOptions["user"]["stats"]["getArtistCommunity"];
type QueryOutput = RouterOutputs["user"]["stats"]["getArtistCommunity"];

/**
 * Bidirectional drift check pinning `artistCommunityWireSchema` to the
 * router output — see `useLibrary/queryFunctions.ts` for rationale.
 */
type _ArtistCommunityWireSchemaSubset =
  z.infer<typeof artistCommunityWireSchema> extends QueryOutput ? true : never;
type _ArtistCommunityWireSchemaSuperset =
  QueryOutput extends z.infer<typeof artistCommunityWireSchema> ? true : never;
const _artistCommunityWireSchemaDriftCheck: [
  _ArtistCommunityWireSchemaSubset,
  _ArtistCommunityWireSchemaSuperset,
] = [true, true];
void _artistCommunityWireSchemaDriftCheck;

export const artistCommunityQueryKey = (inputs: QueryInputs) =>
  getQueryKey(trpc.user.stats.getArtistCommunity, inputs);

export const artistCommunityQuery = (data: DataSession, inputs: QueryInputs) =>
  data.cache.durableQueryOptions({
    queryKey: artistCommunityQueryKey(inputs),
    queryFn: async () => {
      // Wait for auth to initialize before deciding — on cold start
      // `auth.currentUser` may not be populated yet even for a logged-in
      // user, which would otherwise cause a spurious NoAuthError. Community
      // data is user-scoped, so we still throw if auth resolves to no-user.
      await waitForAuthReady(data.auth);
      if (!data.auth.currentUser()) {
        throw new NoAuthError("artistCommunityQuery queryFn");
      }
      return data.api.user.stats.getArtistCommunity.query(inputs);
    },
    schema: artistCommunityWireSchema,
  });

export const useArtistCommunity = (
  inputs: QueryInputs,
  options?: QueryOptions,
) => {
  const data = useData();
  const ready = useInteractionReady();

  // Behavior preserved from the pre-durable-cache hook:
  //   • 30s refetch interval — leaderboard movement updates while open.
  //   • refetchOnMount: true — first paint may use SQLite initialData, but
  //     we always refetch on screen entry so the network shape wins quickly.
  // Durable cache is purely a cold-start fallback; the in-memory react-query
  // cache continues to drive the 30s polling cadence.
  const query = useQuery({
    ...artistCommunityQuery(data, inputs),
    ...options,
    enabled: ready && (options?.enabled ?? true),
    refetchInterval: 30_000,
    refetchOnMount: true,
  });

  return query;
};
